import React from 'react';
import Link from 'next/link';
import { Container, Row, Col } from 'react-bootstrap';
import { FaClock, FaUser, FaArrowLeft, FaCalendarDays } from 'react-icons/fa6';
import Seo from '@/components/seo';
import ShareButtons from '@/components/blog/share-buttons';
import BlogCta from '@/components/blog/blog-cta';
import PostCard from '@/components/blog/post-card';
import { blogRepository } from '@/repositories/blog';

export default function BlogPostPage({ post, relatedPosts = [] }) {
  if (!post) {
    return (
      <Container className="py-5 text-center">
        <h2 className="fw-bold mb-3">Artigo não encontrado</h2>
        <p className="text-muted mb-4">O artigo que você está procurando pode ter sido movido ou excluído.</p>
        <Link href="/blog" className="btn btn-primary rounded-pill px-4">
          Voltar para o Blog
        </Link>
      </Container>
    );
  }

  const dateObj = new Date(post.publishedAt);
  const formattedDate = dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const postUrl = `https://aboutcar.com.br/blog/${post.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.coverImage.startsWith('http') ? post.coverImage : `https://aboutcar.com.br${post.coverImage}`,
    datePublished: post.publishedAt,
    dateModified: post.modifiedAt || post.publishedAt,
    author: {
      '@type': 'Person',
      name: post.author?.name || 'AboutCar',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AboutCar',
      logo: {
        '@type': 'ImageObject',
        url: 'https://aboutcar.com.br/assets/images/aboutcar-classifieds.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: 'https://aboutcar.com.br',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://aboutcar.com.br/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.category?.name || 'Artigos',
        item: `https://aboutcar.com.br/blog/categoria/${post.category?.slug || 'dicas-aboutcar'}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: post.title,
        item: postUrl,
      },
    ],
  };

  return (
    <div className="blog-post-page py-4 bg-light-subtle" style={{ minHeight: '80vh' }}>
      <Seo
        title={`${post.title} | Blog AboutCar`}
        description={post.excerpt}
        canonical={postUrl}
        ogType="article"
        ogImage={post.coverImage}
        schema={[articleSchema, breadcrumbSchema]}
      />

      <Container>
        {/* Navigation & Breadcrumb */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb small mb-0">
              <li className="breadcrumb-item">
                <Link href="/" className="text-decoration-none text-muted">Início</Link>
              </li>
              <li className="breadcrumb-item">
                <Link href="/blog" className="text-decoration-none text-muted">Blog</Link>
              </li>
              {post.category?.slug && (
                <li className="breadcrumb-item">
                  <Link href={`/blog/categoria/${post.category.slug}`} className="text-decoration-none text-muted">
                    {post.category.name}
                  </Link>
                </li>
              )}
              <li className="breadcrumb-item active text-truncate d-none d-md-inline-block text-secondary" style={{ maxWidth: '300px' }} aria-current="page">
                {post.title}
              </li>
            </ol>
          </nav>
          <Link href="/blog" className="btn btn-sm btn-outline-secondary rounded-pill px-3 d-inline-flex align-items-center gap-1">
            <FaArrowLeft size={11} /> Voltar ao Blog
          </Link>
        </div>

        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            {/* Post Header Card */}
            <article className="bg-white rounded-5 p-4 p-md-5 shadow-sm mb-5">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Link
                  href={`/blog/categoria/${post.category?.slug || 'dicas-aboutcar'}`}
                  className="badge bg-primary text-white text-decoration-none rounded-pill px-3 py-2 fw-semibold fs-7"
                >
                  {post.category?.name || 'Dicas'}
                </Link>
                <span className="text-muted small d-inline-flex align-items-center gap-1">
                  <FaClock size={12} />
                  {post.readTime}
                </span>
              </div>

              <h1 className="display-6 fw-bold text-dark mb-4 lh-sm">
                {post.title}
              </h1>

              {/* Author & Date Bar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pb-4 mb-4 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div className="bg-light rounded-circle p-2 text-primary d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                    <FaUser size={16} />
                  </div>
                  <div>
                    <span className="d-block fw-semibold text-dark small">{post.author?.name || 'AboutCar'}</span>
                    <span className="text-muted d-inline-flex align-items-center gap-1 fs-8">
                      <FaCalendarDays size={10} />
                      {formattedDate}
                    </span>
                  </div>
                </div>

                <ShareButtons title={post.title} url={postUrl} />
              </div>

              {/* Cover Image Banner if content has no images */}
              {post.coverImage && !post.content.includes('<img') && (
                <div className="rounded-4 overflow-hidden mb-4 shadow-sm" style={{ maxHeight: '440px' }}>
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover', maxHeight: '440px' }}
                    loading="lazy"
                  />
                </div>
              )}

              {/* Post Content */}
              <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Contextual CTA */}
              <div className="mt-5">
                <BlogCta compact />
              </div>

              {/* Share Footer */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pt-4 mt-5 border-top">
                <div className="fw-semibold text-dark small">Gostou deste conteúdo? Compartilhe:</div>
                <ShareButtons title={post.title} url={postUrl} />
              </div>
            </article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="my-5">
                <h3 className="fw-bold text-dark mb-4">Artigos Relacionados</h3>
                <Row className="g-4">
                  {relatedPosts.map((relPost) => (
                    <Col key={relPost.id} xs={12} md={4}>
                      <PostCard post={relPost} />
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export async function getServerSideProps({ params, res }) {
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400');

  const { slug } = params;
  const post = blogRepository.getPostBySlug(slug);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const relatedPosts = blogRepository.getRelatedPosts(post.category?.slug, post.slug, 3);

  return {
    props: {
      post,
      relatedPosts,
    },
  };
}
